import { useState } from "react";
import { Mail, MapPin, MessageCircle, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { API } from "../../lib/api";

const inputCls = "w-full rounded-xl border border-input bg-background px-4 py-3.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/contacts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success("¡Mensaje enviado! Te responderemos en menos de 24 horas.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("No pudimos enviar tu mensaje. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-form-section" className="py-24 sm:py-32 bg-secondary/30">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <Reveal>
          <p className="text-xs uppercase font-mono tracking-[0.28em] text-emerald-600 dark:text-emerald-400 font-semibold mb-4">Hablemos</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            ¿Dudas sobre qué curso <span className="font-serif italic text-emerald-500">te conviene?</span>
          </h2>
          <p className="text-base text-muted-foreground leading-relaxed mt-5 max-w-md">
            Cuéntanos tu situación y un asesor académico te recomendará la ruta ideal, sin compromiso.
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-500 flex items-center justify-center"><Mail className="w-5 h-5" /></span>
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Correo</p>
                <p className="text-sm font-medium mt-0.5">admisiones@capacitacon.co</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-500 flex items-center justify-center"><MessageCircle className="w-5 h-5" /></span>
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">WhatsApp</p>
                <a data-testid="contact-whatsapp-link" href="https://wa.me/573001234567?text=Hola%2C%20quiero%20informaci%C3%B3n%20sobre%20los%20cursos" target="_blank" rel="noopener noreferrer" className="text-sm font-medium mt-0.5 hover:text-emerald-500 transition-colors">
                  +57 300 123 4567
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-500 flex items-center justify-center"><MapPin className="w-5 h-5" /></span>
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Modalidad</p>
                <p className="text-sm font-medium mt-0.5">100% virtual · Clases en vivo para Latinoamérica</p>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="rounded-3xl border border-border bg-card p-8 shadow-xl space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contact-name" className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Nombre</label>
                <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Tu nombre" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Correo</label>
                <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="tu@correo.com" className={inputCls} />
              </div>
            </div>
            <div>
              <label htmlFor="contact-subject" className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Asunto</label>
              <input id="contact-subject" data-testid="contact-subject-input" required value={form.subject} onChange={set("subject")} placeholder="Ej: Quiero capacitar a mi equipo" className={inputCls} />
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Mensaje</label>
              <textarea id="contact-message" data-testid="contact-message-input" required rows={4} value={form.message} onChange={set("message")} placeholder="Cuéntanos qué necesitas..." className={`${inputCls} resize-none`} />
            </div>
            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-primary text-primary-foreground font-semibold py-4 text-sm hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {loading ? "Enviando..." : "Enviar mensaje"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
